import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { HasMark } from "@/components/BrandText";
import { APPROACH_STEPS, WHY_CHOOSE, IMAGES } from "@/data/site";

export default function Approach() {
  return (
    <div data-testid="page-approach" className="pt-32">
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-16 md:pt-20 md:pb-24">
          <Reveal>
            <div className="overline mb-5">Our Approach</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-4xl md:text-7xl text-white leading-[1.05] tracking-tight max-w-5xl">
              A Disciplined Path To <span className="italic gold-gradient-text">Sustainable Growth</span>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-7 max-w-3xl text-white/70 text-lg leading-relaxed">
              Five carefully orchestrated stages — engineered to turn complex business challenges into clear,
              measurable outcomes.
            </p>
          </Reveal>
        </div>
      </section>

      {/* HORIZONTAL TIMELINE */}
      <section className="relative pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="relative">
            <div className="hidden lg:block absolute top-9 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" />
            <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
              {APPROACH_STEPS.map((st) => {
                const Icon = st.icon;
                return (
                  <Item key={st.num}>
                    <div className="text-left lg:text-center group">
                      <div className="relative z-10 inline-flex items-center justify-center h-[72px] w-[72px] rounded-full border border-[#D4AF37]/40 bg-[#0A0A0A] text-[#D4AF37] font-display text-xl ring-8 ring-[#0A0A0A]">
                        {st.num}
                      </div>
                      <div className="mt-6 inline-flex items-center justify-center h-10 w-10 border border-[#D4AF37]/30 text-[#D4AF37]">
                        <Icon size={18} strokeWidth={1.5} />
                      </div>
                      <h3 className="font-display text-2xl text-white mt-5">{st.title}</h3>
                      <p className="mt-2 text-sm text-white/65 leading-relaxed">{st.desc}</p>
                    </div>
                  </Item>
                );
              })}
            </Stagger>
          </div>
        </div>
      </section>

      {/* DETAIL SECTION */}
      <section className="relative py-24 md:py-32 bg-[#0c0c0c] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-24 h-24 border-l border-t border-[#D4AF37]/60" />
              <div className="absolute -bottom-3 -right-3 w-24 h-24 border-r border-b border-[#D4AF37]/60" />
              <img src={IMAGES.meeting} alt="Strategic meeting" className="w-full h-[460px] md:h-[560px] object-cover grayscale-[15%]" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight leading-tight">
              Built On <span className="italic gold-gradient-text">Evidence</span>. Delivered With <span className="italic gold-gradient-text">Precision</span>.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              Every recommendation we make is grounded in primary research, market intelligence, and operating
              experience. We work alongside your leadership — not in parallel — to ensure execution stays anchored
              to the strategy.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              {[
                ["Senior-Led", "Partners stay on every mandate."],
                ["Confidential", "Strict confidentiality and governance."],
                ["Outcome-Focused", "Measurable KPIs tied to business value."],
                ["Globally Minded", "UAE, India and international reach."],
              ].map(([t, d]) => (
                <div key={t} className="border-l border-[#D4AF37]/40 pl-4">
                  <div className="text-sm text-[#D4AF37] tracking-[0.18em] uppercase">{t}</div>
                  <div className="text-sm text-white/70 mt-1">{d}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel
            overline={<>Why <HasMark /></>}
            title="The Discipline Behind Our Counsel"
            className="mb-14"
          />
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {WHY_CHOOSE.map((w) => {
              const Icon = w.icon;
              return (
                <Item key={w.title}>
                  <div className="lux-card gold-brackets p-6 h-full">
                    <div className="h-12 w-12 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37]">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <h4 className="font-display text-xl text-white mt-5">{w.title}</h4>
                    <p className="mt-2 text-sm text-white/65 leading-relaxed">{w.desc}</p>
                  </div>
                </Item>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28 bg-[#0c0c0c] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl text-white">
              Bring Us Your <span className="italic gold-gradient-text">Hardest Question.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8">
              <Link to="/contact" className="btn-gold" data-testid="approach-cta">
                Start Your Engagement <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
